import { Button } from '@ibsys/kit';
import { ArrowLeftIcon } from 'lucide-react';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { PageContainer } from '../PageContainer';

type Props = {
  /** Открыта ли карточка: на узком экране она показывается вместо списка, на широком — панели видны обе */
  isDetailOpen: boolean;
  /** Возврат к списку с узкого экрана */
  onBack: () => void;
  /** Левая панель — список */
  list: ReactNode;
  /** Правая панель — карточка */
  detail: ReactNode;
  /** Шапка над панелями (поиск, добавление, переключатели): на узком экране скрывается при открытой карточке */
  toolbar?: ReactNode;
  /**
   * Правая панель, когда карточка не выбрана. Если не задана, `detail` рендерится всегда —
   * для карточек, которые показывают пустое состояние сами.
   */
  empty?: ReactNode;
  /** Подпись кнопки возврата; по умолчанию — общая «К списку» */
  backLabel?: string;
  /** Классы левой панели; по умолчанию ширина 320px на широком экране */
  listClassName?: string;
  /** Классы правой панели */
  detailClassName?: string;
};

/**
 * Двухпанельная раскладка «список — карточка».
 *
 * Порог переключения задан container query безымянного контейнера страницы: до него видна одна
 * панель (список либо карточка с кнопкой возврата), после — обе сразу. Имя контейнера не задаётся
 * намеренно: динамическое имя не дошло бы до сборки Tailwind, а ближайшим контейнером для панелей
 * всегда остаётся этот.
 */
export const MasterDetailLayout: FC<Props> = ({
  isDetailOpen,
  onBack,
  list,
  detail,
  toolbar,
  empty,
  backLabel,
  listClassName = '@2xl:w-80',
  detailClassName,
}) => {
  const { t } = useTranslation('common');

  return (
    <PageContainer enableScroll={false} outerClassName="@container h-full" className="flex flex-col h-full gap-4 w-full">
      {toolbar && <div className={cn('flex flex-col gap-4', isDetailOpen && 'hidden @2xl:flex')}>{toolbar}</div>}

      {isDetailOpen && (
        <Button variant="ghost" size="sm" icon={<ArrowLeftIcon />} onClick={onBack} className="@2xl:hidden mr-auto">
          {backLabel ?? t('backToList')}
        </Button>
      )}

      <div className="flex flex-1 gap-6 overflow-hidden">
        <div
          className={cn(
            'flex-col overflow-hidden w-full shrink-0',
            isDetailOpen ? 'hidden @2xl:flex' : 'flex',
            listClassName,
          )}
        >
          {list}
        </div>

        <div
          className={cn(
            'flex-1 overflow-hidden min-w-0',
            isDetailOpen ? 'block' : 'hidden @2xl:block',
            detailClassName,
          )}
        >
          {empty !== undefined && !isDetailOpen ? empty : detail}
        </div>
      </div>
    </PageContainer>
  );
};
